import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import moment from 'moment';

import AppSumoScreen from '../screens/AppSumoScreen';
import { CampaignScreen } from '../screens/CampaignScreen';
import { CampaignsListScreen } from '../screens/CampaignsListScreen';
import ProfileScreen from '../screens/ProfileScreen';
import RegisterScreen from '../screens/RegisterScreen';
import ThankYouPageScreen from '../screens/ThankYouPageScreen';
import CookiePolicy from '../screens/static/CookiePolicy';
import PrivacyPolicy from '../screens/static/PrivacyPolicy';
import TermsAndConditions from '../screens/static/TermsAndConditions';
import { isAmplitudeUserPropertyUserIdExist } from '../utils/analytics/amplitude';
import {
  setUserPropertyConversionStage,
  trackShowSupportChat,
  trackSignUp,
} from '../utils/analytics/analytics';
import {
  AnalyticsEvents,
  ConversionStage,
  ConversionStageType,
} from '../utils/analytics/events';
import { startEventSync, stopEventSync } from '../utils/analytics/eventSync';
import { trackSentryError } from '../utils/analytics/sentry';
import { invalidateCredits } from '../utils/api';
import {
  createAnonymousUserAsync,
  handleRedirectResult,
  isInRedirectFlow,
  shouldExpectRedirectResult,
} from '../utils/auth';
import { colors } from '../utils/colors';
import {
  DEV_LOGIN_PASS_CODE_INSERTED,
  DEV_LOGIN_PASS_CODE_SHA_256_ENCODED,
  IS_DEVELOPMENT_ENV,
} from '../utils/const';
import { hideCrispChat, showCrispChat } from '../utils/crispUtils';
import { firebaseTimestampToDate } from '../utils/dateUtils';
import { useListenUserChanges } from '../utils/db';
import { useFirebaseLoginState, useIsPaidUserStatus } from '../utils/hooks';
import {
  updateFlags,
  updateSession,
  updateStripeUser,
  updateSubscriptionInfo,
} from '../utils/state';
import '../utils/translations';
import { PlanCode, SubscriptionStatus } from '../utils/types';
import DevLogin from './DevLogin';
import Footer from './Footer';
import Header from './Header';

/**
 * Helper function to validate conversion stage with normalization
 * Removes spaces and compares in lowercase to handle variations like 'Advance (monthly)' vs 'Advance(monthly)'
 */
const isValidConversionStage = (stage: string): boolean => {
  if (typeof stage !== 'string') {
    return false;
  }

  // Normalize: remove spaces and convert to lowercase for comparison
  const normalizedStage = stage.replace(/\s+/g, '').toLowerCase();

  // Get normalized values from ConversionStage enum
  const validStagesNormalized = Object.values(ConversionStage).map((value) =>
    value.replace(/\s+/g, '').toLowerCase()
  );

  return validStagesNormalized.includes(normalizedStage);
};

const sessionId = 'session_' + moment().format('YYYY-MM-DD_HH-mm-ss');

// Capture UTM parameters on app initialization
const captureUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmData = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'),
    utm_term: urlParams.get('utm_term'),
  };

  // Only store if at least one UTM parameter is present
  const hasUTMData = Object.values(utmData).some((value) => value !== null);

  if (hasUTMData) {
    localStorage.setItem(
      'signup_attribution',
      JSON.stringify({
        ...utmData,
        timestamp: new Date().toISOString(),
        captured_url: window.location.href,
      })
    );
    console.log('[App | Analytics] UTM parameters captured:', utmData);
  }
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isRegistered, user, userId } = useFirebaseLoginState();
  const signInExecuted = useRef(false);
  const { dbUser, analyticsProperties } = useListenUserChanges(userId);
  const isPaidUser = useIsPaidUserStatus(dbUser?.subscription?.status);

  // Preserve query parameters (UTM, language, etc.) in redirects
  const search = location.search || '';

  // Check dev login
  const isDevLoginValid = () => {
    if (!IS_DEVELOPMENT_ENV) return true;
    const storedCode = Cookies.get(DEV_LOGIN_PASS_CODE_INSERTED);
    return storedCode === DEV_LOGIN_PASS_CODE_SHA_256_ENCODED;
  };

  // Capture UTM parameters on first load
  useEffect(() => {
    captureUTMParameters();
  }, []); // Run once on mount

  // CRITICAL: Handle redirect result immediately on component mount
  useEffect(() => {
    // This must be called FIRST, before any other Firebase operations
    if (shouldExpectRedirectResult()) {
      handleRedirectResult()
        .then((result) => {
          if (result) {
            // Successfully processed redirect result
          }
        })
        .catch((error) => {
          trackSentryError('Failed to process redirect result', { error });
          console.error('🚨 URGENT: Failed to process redirect result:', error);
        });
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const redirectResult = await handleRedirectResult();

        if (redirectResult) {
          // User is now authenticated, skip anonymous user creation
          signInExecuted.current = true;
          return;
        }

        if (!signInExecuted.current && !isInRedirectFlow()) {
          await createAnonymousUserAsync();
          signInExecuted.current = true;
        } else {
          // Skipping anonymous user creation
        }
      } catch (error) {
        trackSentryError('Failed to initialize authentication', { error });
        console.error('❌ Failed to initialize authentication:', error);
        if (!signInExecuted.current && !isInRedirectFlow()) {
          try {
            await createAnonymousUserAsync();
            signInExecuted.current = true;
          } catch (anonymousError) {
            trackSentryError('Failed to create anonymous user', {
              error: anonymousError,
            });
            console.error('❌ Failed to create anonymous user:', anonymousError);
          }
        }
      }
    };

    initializeAuth();
    dispatch(updateSession(sessionId));
  }, []);

  useEffect(() => {
    if (!!userId) {
      try {
        invalidateCredits(userId, sessionId);
      } catch (error) {
        trackSentryError('Failed to invalidate credits', { userId, error });
        console.error('❌ Failed to invalidate credits:', { userId, error });
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!!userId) {
      if (!isAmplitudeUserPropertyUserIdExist()) {
        trackSignUp(userId, AnalyticsEvents.SIGN_UP.values.CATEGORY.FALLBACK).catch(
          (error) => {
            trackSentryError('Failed to track sign up', { userId, error });
            console.error('[App | Analytics] Failed to track sign up:', error);
          }
        );
      }
    }
  }, [isRegistered]);

  useEffect(() => {
    if (!!user) {
      Sentry.setUser({
        id: userId,
        email: user?.email || undefined,
        username: user?.displayName || undefined,
      });
    } else {
      Sentry.getGlobalScope().clear();
    }
  }, [user]);

  useEffect(() => {
    if (dbUser?.stripe) {
      dispatch(
        updateStripeUser({
          amountTotal: dbUser?.stripe?.amount_total,
          cancelAtPeriodEnd: dbUser?.stripe?.cancel_at_period_end,
          cancelAt: dbUser?.stripe?.cancel_at,
          currencyCode: dbUser?.stripe?.currency_code,
          customerId: dbUser?.stripe?.customer_id,
          planId: dbUser?.stripe?.plan_id,
          planInterval: dbUser?.stripe?.plan_interval,
          subscriptionName: dbUser?.stripe?.subscription_name,
          current_period_start: dbUser?.stripe?.current_period_start,
          current_period_end: dbUser?.stripe?.current_period_end,
        })
      );
      const conversionStage = dbUser?.stripe?.subscription_name;
      if (conversionStage && isValidConversionStage(conversionStage)) {
        setUserPropertyConversionStage(conversionStage as ConversionStageType);
      } else if (conversionStage) {
        trackSentryError('Invalid conversion stage value', { conversionStage });
      }
    }
  }, [JSON.stringify(dbUser?.stripe)]);

  useEffect(() => {
    if (dbUser?.subscription) {
      const data = {
        status: dbUser?.subscription?.status as SubscriptionStatus,
        credits: dbUser?.subscription?.credits,
        planCode: dbUser?.subscription?.plan_code as PlanCode,
        dateStart: moment(
          firebaseTimestampToDate(dbUser?.subscription?.start_date)
        ).toDate(),
        dateEnd: moment(
          firebaseTimestampToDate(dbUser?.subscription?.end_date)
        ).toDate(),
        dateCycleEnd: moment(
          firebaseTimestampToDate(dbUser?.subscription?.cycle_end_date)
        ).toDate(),
        dateCycleStart: moment(
          firebaseTimestampToDate(dbUser?.subscription?.cycle_start_date)
        ).toDate(),
      };
      dispatch(updateSubscriptionInfo(data));
      dispatch(
        updateFlags({ isUserMigratedToCredits: dbUser?.flags?.is_migrated_to_credits })
      );
    }
  }, [JSON.stringify(dbUser?.subscription)]);

  useEffect(() => {
    // Avoid tracking until we know the userId to prevent "undefined" labels
    if (!userId) {
      hideCrispChat();
      return;
    }
    if (isPaidUser) {
      showCrispChat();
      trackShowSupportChat(userId, true).catch((error) => {
        trackSentryError('Failed to track support chat shown', { userId, error });
        console.error('[App | Analytics] Failed to track support chat shown:', error);
      });
    } else {
      hideCrispChat();
    }
  }, [isPaidUser, userId]);

  // New useEffect for handling analytics properties changes from Firestore
  useEffect(() => {
    if (analyticsProperties && userId) {
      // Analytics properties have been updated from backend via Firestore
      // The updateAnalyticsFromFirestore function in useListenUserChanges handles the updates
      console.log(
        '[App | Analytics] Properties updated from backend:',
        analyticsProperties
      );
    }
  }, [analyticsProperties, userId]);

  // Start/stop analytics event syncing when user changes
  useEffect(() => {
    if (userId) {
      console.log(`[App | Analytics] Starting event sync for user: ${userId}`);
      startEventSync(userId);
    } else {
      console.log('[App | Analytics] Stopping event sync - no user');
      stopEventSync();
    }

    // Cleanup function to stop syncing when component unmounts or user changes
    return () => {
      stopEventSync();
    };
  }, [userId]);

  if (IS_DEVELOPMENT_ENV && !isDevLoginValid()) {
    return <DevLogin />;
  }

  return (
    <Box sx={{ minWidth: '1024px', overflow: 'scroll' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          // bgcolor: window.location.pathname.includes('/campaigns') ? colors.custom.campaigns : 'background.default',
          bgcolor: colors.custom.campaigns,
        }}
      >
        <Header />
        <main style={{ flex: 1 }}>
          <Routes>
            {/* Main app routes */}
            <Route
              path="/app/*"
              element={
                <Routes>
                  {/* Main app routes */}
                  <Route
                    index
                    element={<Navigate replace to={`/app/campaigns${search}`} />}
                  />
                  <Route path="register" element={<RegisterScreen />} />
                  <Route path="thankyou" element={<ThankYouPageScreen />} />
                  <Route path="appsumo" element={<AppSumoScreen />} />
                  <Route path="campaigns" element={<CampaignsListScreen />} />
                  <Route path="campaigns/:campaignId" element={<CampaignScreen />} />
                  <Route path="profile/:activeTabSlug?" element={<ProfileScreen />} />

                  {/* Static policy pages */}
                  <Route path="cookie-policy" element={<CookiePolicy />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="terms-and-conditions" element={<TermsAndConditions />} />

                  {/* Catch any unknown paths under /app and redirect to /app/campaigns */}
                  <Route
                    path="*"
                    element={<Navigate replace to={`/app/campaigns${search}`} />}
                  />
                </Routes>
              }
            />
            {/* Redirect root and all other unknown paths to /app/campaigns */}
            <Route
              path="/"
              element={<Navigate replace to={`/app/campaigns${search}`} />}
            />
            <Route
              path="*"
              element={<Navigate replace to={`/app/campaigns${search}`} />}
            />
          </Routes>
        </main>
        <Footer />
      </Box>
    </Box>
  );
};

export default App;
